import React, { useState, Fragment, useEffect, useMemo, useRef, useContext } from "react";
import { IdeaBox, IdeaBoxEntry, Project, Task, TaskType } from 'konect-model';
import {
    MDBTable,
    MDBTableBody,
    MDBTableHead,
    MDBIcon,
    MDBBtn,
    MDBDropdownToggle, MDBDropdownMenu, MDBDropdownItem, MDBDropdownLink, MDBDropdown, MDBInput
} from 'mdb-react-ui-kit';
import IdeaBoxRow from "./IdeaBoxRow";
import { AppContext, NavState } from "../App";
import UserControlService from "../../services/user-control-service";
import "./IdeaBoxView.css";
import { nanoid } from "nanoid";
import { InfoWindowState } from "./InfoWindow";

interface IdeaboxViewProps {
    type: TaskType;
}

export default function IdeaBoxView(props: IdeaboxViewProps) {

    const { setShowInfo, project, setProject, navState, setNavState, setEditingTask } = useContext(AppContext);

    const ideaBox: IdeaBox = project.ideaBox;

    const entryBackup = useRef<IdeaBoxEntry>(new IdeaBoxEntry());

    const [filterString, setFilterString] = useState("");

    const [entries, setEntries] = useState([...ideaBox.entries.filter(el => el.type === props.type && el.task.name.toLowerCase().includes(filterString))]);

    const [entry, setEntry] = useState<IdeaBoxEntry>(new IdeaBoxEntry());

    const [insights, setInsights] = useState(project.insights.filter(el => el.type === props.type || (!el.type && props.type === TaskType.INFORMATION)));

    const [filter, setFilter] = useState("sort by");

    const updateEntries = function (e: IdeaBoxEntry[]) {
        setEntries(sortBy([...e.filter(el => el.type === props.type && el.task.name.toLowerCase().includes(filterString))], filter));
    }

    useEffect(() => {
        updateEntries(project.ideaBox.entries);
        setInsights(project.insights.filter(el => el.type === props.type || (!el.type && props.type === TaskType.INFORMATION)));
    }, [props.type, filter, project, filterString]);  // eslint-disable-line react-hooks/exhaustive-deps

    // refresh link between task object in taskModel and ideaBoxEntry
    useEffect(() => {
        entries.forEach((entry) => {
            entry.task = Task.findTask(project.taskModel.rootTask, entry.task.id) || entry.task;
        });
    }, []);

    const updateEntry = (event: any) => {
        UserControlService.update();
        switch (event.target.name) {
            case "insight":
                const insight = JSON.parse(event.target.dataset["mssg"]);
                entry.insight = insight;
                entry.visualLevel = insight.visualLevel;
                entry.availableAttributes = project.getAttributesOfInsight(insight);
                entry.selectedAttribute = entry.availableAttributes[0];
                break;
            case "selectedAttribute":
                const attribute = JSON.parse(event.target.dataset["mssg"]);
                entry.selectedAttribute = attribute;
                break;
            default:
                //@ts-ignore
                entry[event.target.name] = event.target.value;
        }
        IdeaBox.updateEntry(ideaBox, entry.id, { ...entry });
        setEntry({ ...entry });
    };

    const submitEntry = (event: React.FormEvent) => {
        event.preventDefault();
        setEntry(new IdeaBoxEntry());
        updateEntries(ideaBox.entries);
    };

    const initEdit = (event: MouseEvent, entry: IdeaBoxEntry) => {
        event.preventDefault();
        entryBackup.current = { ...entry };
        setEntry(entry);
        updateEntries(ideaBox.entries);
    };

    const cancelEdit = (e: MouseEvent) => {
        e.preventDefault();
        IdeaBox.updateEntry(ideaBox, entry.id, { ...entryBackup.current });
        setEntry(new IdeaBoxEntry());
        updateEntries(ideaBox.entries);
    };

    const handleInfo = (event: MouseEvent, type: any) => {
        event.stopPropagation();
        console.log("Handle Info: " + type);
        setShowInfo(InfoWindowState.IDEA_BOX);
    }

    const isInconsistentEntry = (entry: IdeaBoxEntry) => {
        if (!entry.insight || !entry.selectedAttribute) {
            return false;
        }
        return !!entries.find(el =>
            el.insight && el.selectedAttribute
            && el.insight.id === entry.insight.id
            && el.selectedAttribute.id !== entry.selectedAttribute.id);
    }

    const applyFilter = (event: MouseEvent) => {
        setFilter((event.target as HTMLInputElement).value);
    }

    const sortBy = (e: IdeaBoxEntry[], type: string) => {
        const sortByAttribute = (attribute: string) => {
            e.sort((a, b) => {
                //@ts-ignore
                if (!b[attribute]) {
                    return false;
                }
                if (attribute === "taskName") {
                    //@ts-ignore
                    return !a[attribute] || a[attribute].localeCompare(b[attribute]);
                }
                //@ts-ignore
                return !a[attribute] || a[attribute].name.localeCompare(b[attribute].name);
            });
        }
        switch (type) {
            case "ATTRIBUTE":
                sortByAttribute("selectedAttribute");
                break
            case "INSIGHT":
                sortByAttribute("insight");
                break
            case "TASK":
                sortByAttribute("taskName");
                break
            default:
                break;
        }
        return e;
    }

    const goToTask = (id: string) => {
        UserControlService.changeView(NavState.TASK);
        setTimeout(() => {
            var container = document.getElementById("graph-container");
            var node = document.getElementById(id);
            if (container && node) {
                const containerRect = container.getBoundingClientRect();
                const nodeRect = node.getBoundingClientRect();
                const verticalScrollPosition = nodeRect.top - containerRect.top + container.scrollTop - (containerRect.height / 2) + (nodeRect.height / 2);
                const horizontalScrollPosition = nodeRect.left - containerRect.left + container.scrollLeft - (containerRect.width / 2) + (nodeRect.width / 2);
                container.scrollTop = verticalScrollPosition;
                container.scrollLeft = horizontalScrollPosition;
                //@ts-ignore
                setTimeout(() => {node.style.backgroundColor = "#FE3452";}, 500);
                //@ts-ignore
                setTimeout(() => {node.style.backgroundColor = null}, 4500);
            }
        }, 1);
    }

    const editEntry = (event: Event, entry: IdeaBoxEntry) => {
        event.stopPropagation();
        const task = Task.findTask(project.taskModel.rootTask, entry.task.id);
        if (!task) return;
        goToTask(task.id);
        setEditingTask(task);
    }

    const duplicateEntry = (event: Event, entry: IdeaBoxEntry) => {
        event.stopPropagation();
        UserControlService.update();
        const task = Task.findTask(project.taskModel.rootTask, entry.task.id);
        if (!task) return;
        Task.addTaskRule(project, task, nanoid(), entry.interfaceElement, [entry.type], project.ideaBox.entries.indexOf(entry));
        setProject(project);
    }

    const deleteEntry = (event: Event, entry: IdeaBoxEntry) => {
        event.stopPropagation();
        UserControlService.update();
        IdeaBox.removeIdeaBoxEntry(project, entry.ruleId)
        const task = Task.findTask(project.taskModel.rootTask, entry.task.id);
        if (!task) return;
        Task.removeTaskRule(project, task, entry.ruleId);
        setProject(project);
    }

    const renderEntries = useMemo(() => {

        return entries.map((e) => {
            const hasTask = !!Task.findTask(project.taskModel.rootTask, e.task.id);
            return <Fragment key={e.id}>
              
                    <IdeaBoxRow keyy={e.id}
                        entry={e}
                        props={props}
                        onInitEdit={initEdit}
                        goToTask={goToTask}
                        editEntry={editEntry}
                        duplicateEntry={duplicateEntry}
                        deleteEntry={deleteEntry}
                        isInconsistent={isInconsistentEntry(e)}
                        hasTask={hasTask}
                        onUpdate={updateEntry}
                        onAbort={cancelEdit}
                        insights={insights}
                    />
               
            </Fragment>
        })
    }, [entry, entries]);  // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Fragment>
            <div className="d-flex align-items-center w-100 py-2">
                <div className="w-25"></div>
                <div className="ib-button-wrapper">
                    <div className="ib-button-border">
                        <button type="button" className={`ib-button ${navState === NavState.IDEABOX_1 ? "ib-button-active" : "ib-button-inactive"}`} onClick={() => setNavState(NavState.IDEABOX_1)}>PERCEPTION BOX</button>
                        <button type="button" className={`ib-button ${navState === NavState.IDEABOX_2 ? "ib-button-active" : "ib-button-inactive"}`} onClick={() => setNavState(NavState.IDEABOX_2)}>INTERACTION BOX</button>
                        <div style={{ transform: `translate(${navState === NavState.IDEABOX_1 ? "0%" : "100%"}` }} className="ib-slider"></div>
                    </div>
                </div>
                <div className="position-relative d-flex align-items-center justify-content-center w-25">
                    <MDBInput id='search-input' label='Search Task Name' wrapperClass="w-100" type="text" value={filterString} onChange={(e: any) => setFilterString(e.target.value.toLowerCase())} style={{}} />
                    {filterString && <div onClick={() => { setFilterString(""); document.getElementById('search-input')?.blur(); }} id='clear-textfield' className="position-absolute end-0 h-100 d-flex align-items-center justify-content-center" style={{ width: "35px" }}>
                        <i className="fas fa-times secondary"></i>
                    </div>}
                </div>
            </div>

            <div className="app-container overflow-auto">
                <form onSubmit={submitEntry}>
                    <MDBTable /*striped*/ hover small>
                        <MDBTableHead style={{
                            position: 'sticky',
                            top: '0',
                            backgroundColor: 'white',
                            zIndex: "1",
                            verticalAlign: "middle",
                            userSelect: "none"
                        }}>
                            <tr key="-1">
                                {/*<th>ID</th>*/}
                                <th onClick={(e) => { sortBy(entries, "TASK") }}>Task</th>
                                <th>Interface Element</th>
                                <th onClick={(e) => { sortBy(entries, "INSIGHT") }}>
                                    Insight
                                    <MDBBtn floating outline size='sm' className='mx-1'
                                        onClick={(event: MouseEvent) => handleInfo(event, "insight")}>
                                        <MDBIcon fas icon='info' size='xs' />
                                    </MDBBtn>
                                </th>
                                {props.type === TaskType.INTERACTION && false &&
                                    <Fragment>
                                        <th style={{ textAlign: "center" }}>Fail-Safety</th>
                                        <th>Restrictions</th>
                                    </Fragment>}
                                <th onClick={(e) => { sortBy(entries, "ATTRIBUTE") }}>
                                    Selected Attribute
                                    <MDBBtn floating outline size='sm' className='mx-1' onClick={(event: MouseEvent) => handleInfo(event, "attributes")}>
                                        <MDBIcon fas icon='info' size='xs' />
                                    </MDBBtn>
                                </th>
                                <th style={{ textAlign: "center" }}>
                                    <MDBDropdown>
                                        <MDBDropdownToggle type="button">{filter || "select filter"}</MDBDropdownToggle>
                                        <MDBDropdownMenu>
                                            <MDBDropdownItem key={"di_insight"}>
                                                <MDBDropdownLink name="default"
                                                    onClick={applyFilter}
                                                    value={"sort by"} renderer={"DEFAULT"}
                                                    tag="button" type="button"> DEFAULT
                                                </MDBDropdownLink>
                                                <MDBDropdownLink name="task"
                                                    onClick={applyFilter}
                                                    value={"TASK"} renderer={"TASK"}
                                                    tag="button" type="button"> TASK
                                                </MDBDropdownLink>
                                                <MDBDropdownLink name="insight"
                                                    onClick={applyFilter}
                                                    value={"INSIGHT"} renderer={"INSIGHT"}
                                                    tag="button" type="button"> INSIGHT
                                                </MDBDropdownLink>
                                                <MDBDropdownLink name="attribute"
                                                    onClick={applyFilter}
                                                    value={"ATTRIBUTE"} renderer={"ATTRIBUTE"}
                                                    tag="button" type="button"> ATTRIBUTE
                                                </MDBDropdownLink>
                                            </MDBDropdownItem>
                                        </MDBDropdownMenu>
                                    </MDBDropdown>
                                </th>
                            </tr>
                        </MDBTableHead>
                        <MDBTableBody style={{ fontSize: '0.85rem', maxHeight: '50vh', verticalAlign: "middle" }}
                            className="overflow-auto">
                            {renderEntries}
                        </MDBTableBody>
                    </MDBTable>
                </form>
            </div>
        </Fragment>
    );
};